import type { FunnelStage } from '../core/types/funnel-stage.js'

type FunnelStageTranslator = (key: string) => string

const defaultFunnelStageTranslator: FunnelStageTranslator = key => {
  switch (key) {
    case 'funnelStage.discovery':
      return 'Understanding'
    case 'funnelStage.evaluatingFit':
      return 'Exploring'
    case 'funnelStage.workshopPitch':
      return 'Recommendations'
    case 'funnelStage.consultingPitch':
      return 'Recommendations'
    case 'funnelStage.proposal':
      return 'Recommendations'
    case 'funnelStage.objections':
      return 'Exploring'
    case 'funnelStage.closing':
      return 'Report'
    case 'funnelStage.summary':
      return 'Report'
    case 'funnelStage.followUp':
      return 'Follow-up'
    default:
      return key
  }
}

/** Lead-facing agent label for the active funnel stage (no internal scoring jargon). */
export function getAgentActivityLabel(
  stage: FunnelStage | undefined,
  t: FunnelStageTranslator = defaultFunnelStageTranslator
): string | null {
  if (!stage) return null

  switch (stage) {
    case 'DISCOVERY':
    case 'INTELLIGENCE_GATHERING':
      return t('funnelStage.discovery')
    case 'SCORING':
    case 'QUALIFIED':
      return t('funnelStage.evaluatingFit')
    case 'WORKSHOP_PITCH':
      return t('funnelStage.workshopPitch')
    case 'CONSULTING_PITCH':
    case 'PITCHING':
      return t('funnelStage.consultingPitch')
    case 'PROPOSAL':
      return t('funnelStage.proposal')
    case 'OBJECTION':
      return t('funnelStage.objections')
    case 'CLOSING':
    case 'BOOKING_REQUESTED':
      return t('funnelStage.closing')
    case 'BOOKED':
    case 'SUMMARY':
      return t('funnelStage.summary')
    case 'RETARGETING':
      return t('funnelStage.followUp')
    default:
      return null
  }
}

/** Visitor-visible agent activity (session chrome) — real product signals, not a parallel wizard. */
export type VisitorSessionActivity = 'researching' | 'writing_brief' | 'working' | 'ready'

/** Canonical report.status values that can drive the activity chip. */
export type VisitorReportStatus =
  | 'none'
  | 'requested'
  | 'generating'
  | 'completed'
  | 'failed'
  | null
  | undefined

/**
 * Latest client prepare attempt ended without a brief.
 * These beat a projection still stuck on requested/generating.
 */
export type BriefPrepareTerminal = 'failed' | 'deferred' | 'timed_out' | 'aborted'

export function isBriefPrepareTerminal(
  value: string | null | undefined,
): value is BriefPrepareTerminal {
  return (
    value === 'failed' ||
    value === 'deferred' ||
    value === 'timed_out' ||
    value === 'aborted'
  )
}

/**
 * WRITING BRIEF from the prepare boolean.
 * A projection left on requested/generating after 409, 422, timeout, or abort
 * must not keep the chip spinning. completed/failed are finished.
 * A still-running report tool is handled by the caller: do not pass that
 * tool name once prepareTerminal is set.
 */
export function isVisitorWritingBrief(input: {
  isPreparingReport?: boolean | undefined
  reportStatus?: VisitorReportStatus
  prepareTerminal?: BriefPrepareTerminal | null | undefined
}): boolean {
  if (input.reportStatus === 'completed' || input.reportStatus === 'failed') return false
  if (isBriefPrepareTerminal(input.prepareTerminal)) return false
  return input.isPreparingReport === true
}

/**
 * Running tool for the activity chip.
 * A finished report tool name is ignored. After prepare fails, times out,
 * aborts, or is deferred, a still-listed writing tool must not keep the chip spinning.
 */
export function activityToolForVisitorChip(input: {
  tools: readonly { name?: string | null | undefined; status?: string | null | undefined }[] | null | undefined
  prepareTerminal?: BriefPrepareTerminal | null | undefined
}): string | undefined {
  const running = runningActivityToolName(input.tools)
  if (isBriefPrepareTerminal(input.prepareTerminal) && isWritingBriefActivityTool(running)) {
    return undefined
  }
  return running
}

/** Only a tool that is still running may flip the activity chip. Completed names do not. */
export function runningActivityToolName(
  tools: readonly { name?: string | null | undefined; status?: string | null | undefined }[] | null | undefined,
): string | undefined {
  const running = tools?.find(
    tool => tool.status === 'running' && typeof tool.name === 'string' && tool.name.trim().length > 0,
  )
  return typeof running?.name === 'string' ? running.name : undefined
}

/** True when the strip should read as “working” (copper + live pulse), not idle Ready. */
export function isVisitorSessionWorking(activity: VisitorSessionActivity): boolean {
  return activity !== 'ready'
}

function normalizeTool(activeTool: string | null | undefined): string {
  return (activeTool || '').trim().toLowerCase()
}

/** Tool names that mean company/web intelligence is in flight (latency-sensitive). */
export function isResearchActivityTool(activeTool: string | null | undefined): boolean {
  const tool = normalizeTool(activeTool)
  if (!tool) return false
  return /research|search|preflight|intelligence|enrich|lookup|crawl|scrape|brreg|company_context|web_/.test(
    tool
  )
}

/** Tool names that mean brief/report/memo generation is in flight. */
export function isWritingBriefActivityTool(activeTool: string | null | undefined): boolean {
  const tool = normalizeTool(activeTool)
  if (!tool) return false
  if (
    /generate_discovery_report|discovery_report|summary_agent|writing.?brief|executive_memo|generate_summary|generate_proposal|generate_executive/.test(
      tool
    )
  ) {
    return true
  }
  // Generic *report* tools — exclude readiness probes so they don't steal the strip.
  return tool.includes('report') && !tool.includes('readiness')
}

/**
 * Map live session signals → visitor status.
 * Booleans win (authoritative); activeTool fills the gap so the strip flips
 * as soon as a tool starts (latency perception — avoid dead “Ready” silence).
 */
export function resolveVisitorSessionActivity(input: {
  isResearching?: boolean | undefined
  isWritingBrief?: boolean | undefined
  activeTool?: string | null | undefined
}): VisitorSessionActivity {
  if (input.isResearching) return 'researching'
  if (input.isWritingBrief) return 'writing_brief'

  if (isResearchActivityTool(input.activeTool)) return 'researching'
  if (isWritingBriefActivityTool(input.activeTool)) return 'writing_brief'

  return 'ready'
}

/** Ready → working when the last visitor turn is still waiting on an assistant reply. */
export function resolveVisitorSessionActivityWithPending(input: {
  isResearching?: boolean | undefined
  isWritingBrief?: boolean | undefined
  activeTool?: string | null | undefined
  pendingReply?: boolean | undefined
}): VisitorSessionActivity {
  const resolved = resolveVisitorSessionActivity(input)
  if (resolved !== 'ready') return resolved
  return input.pendingReply ? 'working' : 'ready'
}

export function getVisitorSessionActivityLabel(
  activity: VisitorSessionActivity,
  t: FunnelStageTranslator = key => {
    switch (key) {
      case 'sessionActivity.researching':
        return 'Researching…'
      case 'sessionActivity.writingBrief':
        return 'Writing brief…'
      case 'sessionActivity.working':
        return 'Working…'
      case 'sessionActivity.ready':
        return 'Ready'
      default:
        return key
    }
  }
): string {
  switch (activity) {
    case 'researching':
      return t('sessionActivity.researching')
    case 'writing_brief':
      return t('sessionActivity.writingBrief')
    case 'working':
      return t('sessionActivity.working')
    case 'ready':
      return t('sessionActivity.ready')
  }
}
